import React, {useReducer, useState} from 'react';
import {ContentAdDesktop} from 'app/styles/content';
import {useFetchData, useRestoreData, useEffectAfterMount} from 'app/hooks/dataHooks';
import {fetchMyMatches, overwriteMyMatches} from 'app/routes/matchCalendar/myMatches/MyMatchesPageActions';
import {MyMatchesPageReducer} from 'app/routes/matchCalendar/myMatches/MyMatchesPageReducer';
import {MatchdayCard} from 'app/components/matchCalendar/matchdayCard';
import {StickyHeader} from 'app/components/matchCalendar/stickyHeader';
import {ButtonGroup} from 'app/components/button/ButtonGroup';
import {useHistory} from 'react-router';
import queryString from 'query-string';
import {LoadingIndicator, LOADING_MESSAGE} from '@fupa/fupa-uikit';
import {useInfiniteScrollComponentState} from 'app/hooks/useInfiniteScroll';
import {StreamPlaceholder} from 'app/styles/stream';
import {Helmet} from 'react-helmet-async';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {MyMatchesPlaceholder} from 'app/components/matchCalendar/MyMatchesPlaceholder';
import {Add} from '@mui/icons-material';
import {MY_MATCHES_SEARCH_ENTITIES} from 'app/components/follow/followHelper';
import {formatDateWithWeekday} from 'app/helpers/dateHelpers';
import {FollowsSyncPlaceholder} from 'app/components/matchCalendar/FollowsSyncPlaceholder';
import {useSelector} from 'react-redux';
import {selectCurrentUser} from 'app/hooks/reduxCreateSelectorHooks';
import {MyMatchesNoFollowsPlaceholder} from 'app/components/matchCalendar/MyMatchesNoFollowsPlaceholder';
import {MatchRow} from 'app/components/match/matchRow/MatchRow';
import {MatchRowsCardWrapper} from 'app/components/match/matchRow/MatchRowStyles';

const getDirection = dir => (dir === 'prev' ? 'prev' : 'next');

const MyMatchesPage = () => {
  const meta = [
    {name: 'description', content: 'Alle Spiele und Liveticker deiner Favoriten auf einen Blick.'},
    {name: 'robots', content: 'noindex, follow'},
  ];
  const history = useHistory();
  const searchQuery = queryString.parse(history.location.search);
  const [activeButton, setActiveButton] = useState(getDirection(searchQuery.dir));
  const [isToggleButtonNav, setIsToggleButtonNav] = useState(false);
  const {restoredData} = useRestoreData('MyMatchesPage');
  const {followsFetched, follows} = useSelector(selectCurrentUser);

  const initData = {
    isFetching: false,
    error: null,
    nextUrl: '',
    items: [],
  };

  const initialState = restoredData ?? initData;

  const [state, dispatch] = useReducer(MyMatchesPageReducer, initialState);

  const loadData = (reloading, reset) => {
    const dir = activeButton;
    const componentState = reset ? initData : state;
    return fetchMyMatches(dir, reloading)(dispatch, componentState);
  };

  const {isFetching, error, items} = state;
  useInfiniteScrollComponentState(loadData, items.length);
  useFetchData(state, loadData, 'MyMatchesPage');

  useEffectAfterMount(() => {
    // Request data on switching matches by navigation with toggle buttons
    const direction = getDirection(searchQuery.dir);
    setIsToggleButtonNav(true);
    setActiveButton(direction);
    dispatch(overwriteMyMatches(initData));
    loadData(null, true).then(() => setIsToggleButtonNav(false));
  }, [history.location]);

  const placeholderAction = {
    label: 'Favoriten hinzufügen',
    onClick: () => history.push('/search', {filters: MY_MATCHES_SEARCH_ENTITIES}),
    actionIcon: <Add color='icon-invert' />,
  };

  const buttons = [
    {
      url: `/my/matches`,
      label: 'Aktuelle Spiele',
      value: 'next',
    },
    {
      url: `/my/matches?dir=prev`,
      label: 'Vergangene Spiele',
      value: 'prev',
    },
  ];

  const noFollowsPlaceholder =
    followsFetched && follows.some(follow => ['competition', 'match', 'team'].includes(follow.entity)) ? (
      <FollowsSyncPlaceholder loadData={loadData} />
    ) : (
      <MyMatchesNoFollowsPlaceholder />
    );

  const placeholder =
    error?.status === 404 ? (
      noFollowsPlaceholder
    ) : !error ? (
      <MyMatchesPlaceholder
        message={`Keine ${activeButton === 'prev' ? 'vergangenen' : 'aktuellen'} Spiele deiner Favoriten vorhanden.`}
        action={placeholderAction}
      />
    ) : null;

  const loadingIndicatorRequired = isFetching || (!followsFetched && error?.status === 404);

  const showPlaceholder = loadingIndicatorRequired ? null : placeholder;

  let previousDate = '';
  const matchdays = items.length
    ? items.map(item => {
        const {date, ...competitionGroup} = item;
        const addHeader = date !== previousDate ? <StickyHeader title={formatDateWithWeekday(date)} /> : null;
        previousDate = date;
        const key = `${competitionGroup.competition.id}-${competitionGroup.round.id}-${date}`;
        return (
          <React.Fragment key={key}>
            {addHeader}
            <MatchdayCard
              competitionGroup={competitionGroup}
              extendWithAgeGroup={competitionGroup.competition.nameSuffix}>
              <MatchRowsCardWrapper>
                {competitionGroup.matches.map(match => (
                  <MatchRow key={match.slug} match={match} colored={false} />
                ))}
              </MatchRowsCardWrapper>
            </MatchdayCard>
          </React.Fragment>
        );
      })
    : showPlaceholder;

  const showLoadingIndicator = loadingIndicatorRequired ? (
    <LoadingIndicator bgColor message={LOADING_MESSAGE.loadingMatches} />
  ) : null;

  const showError =
    error && error.status !== 404 ? (
      <StreamPlaceholder>
        Beim Laden {items.length ? 'weiterer' : 'der'} Spiele ist ein Fehler aufgetreten.
      </StreamPlaceholder>
    ) : null;

  // Show toggle buttons in these cases:
  // - if there are matches -> items.length
  // - if there are no matches but favorites -> !isFetching && !error
  // - if toggle-buttons are used to navigate -> isToggleButtonNav
  const showButtonGroup =
    items.length || (!isFetching && !error) || isToggleButtonNav ? (
      <ButtonGroup activeValue={activeButton} onChange={value => setActiveButton(value)} buttons={buttons} />
    ) : null;

  return (
    <ContentAdDesktop bgColor top='7.125rem' contentPaddingBottom={'4.5rem'}>
      <Helmet title={`Meine Spiele${TITLE_POSTFIX}`} meta={meta} />
      {showButtonGroup}
      {matchdays}
      {showLoadingIndicator}
      {showError}
    </ContentAdDesktop>
  );
};

export default MyMatchesPage;
